import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Plus, MoreVertical, Pencil, Trash2, ArrowRightCircle, Phone, Mail, CalendarClock, Building2, Contact2,
} from "lucide-react";

const STAGES = [
  { k: "new", label: "New", dot: "#71717a" },
  { k: "contacted", label: "Contacted", dot: "#0ea5e9" },
  { k: "meeting", label: "Meeting", dot: "#7c3aed" },
  { k: "quoted", label: "Quoted", dot: "#d97706" },
  { k: "won", label: "Won", dot: "#16a34a" },
  { k: "lost", label: "Lost", dot: "#e11d48" },
];
const SOURCES = [
  ["referral", "Referral"], ["website", "Website"], ["instagram", "Instagram"],
  ["walk_in", "Walk-in"], ["repeat", "Repeat client"], ["other", "Other"],
];
const PTYPES = [["architecture", "Architecture"], ["interior", "Interior"]];
const EMPTY = {
  name: "", company: "", email: "", phone: "", source: "referral",
  project_type: "architecture", estimated_value: "", notes: "", next_follow_up: "", stage: "new",
};
const money = (n) => (n ? `$${Number(n).toLocaleString()}` : "");

function LeadForm({ value, onChange }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  return (
    <div className="space-y-3 py-1">
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Contact name *</label>
          <Input value={value.name} onChange={(e) => set("name", e.target.value)} className="rounded-sm mt-1.5 h-9" data-testid="lead-name-input" />
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Company</label>
          <Input value={value.company} onChange={(e) => set("company", e.target.value)} placeholder="Optional" className="rounded-sm mt-1.5 h-9" data-testid="lead-company-input" />
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Email</label>
          <Input value={value.email} onChange={(e) => set("email", e.target.value)} placeholder="Optional" className="rounded-sm mt-1.5 h-9" data-testid="lead-email-input" />
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Phone</label>
          <Input value={value.phone} onChange={(e) => set("phone", e.target.value)} placeholder="Optional" className="rounded-sm mt-1.5 h-9" data-testid="lead-phone-input" />
        </div>
      </div>
      <div className="grid sm:grid-cols-3 gap-3">
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Source</label>
          <Select value={value.source} onValueChange={(v) => set("source", v)}>
            <SelectTrigger className="rounded-sm mt-1.5 h-9" data-testid="lead-source-select"><SelectValue /></SelectTrigger>
            <SelectContent>{SOURCES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Project type</label>
          <Select value={value.project_type} onValueChange={(v) => set("project_type", v)}>
            <SelectTrigger className="rounded-sm mt-1.5 h-9" data-testid="lead-ptype-select"><SelectValue /></SelectTrigger>
            <SelectContent>{PTYPES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Est. value ($)</label>
          <Input type="number" value={value.estimated_value} onChange={(e) => set("estimated_value", e.target.value)} placeholder="0" className="rounded-sm mt-1.5 h-9" data-testid="lead-value-input" />
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Stage</label>
          <Select value={value.stage} onValueChange={(v) => set("stage", v)}>
            <SelectTrigger className="rounded-sm mt-1.5 h-9" data-testid="lead-stage-select"><SelectValue /></SelectTrigger>
            <SelectContent>{STAGES.map((s) => <SelectItem key={s.k} value={s.k}>{s.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Next follow-up</label>
          <Input type="date" value={value.next_follow_up} onChange={(e) => set("next_follow_up", e.target.value)} className="rounded-sm mt-1.5 h-9" data-testid="lead-followup-input" />
        </div>
      </div>
      <div>
        <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Notes</label>
        <Textarea value={value.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Optional" className="rounded-sm mt-1.5 text-sm" data-testid="lead-notes-input" />
      </div>
    </div>
  );
}

export default function CRM() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/leads").then((r) => setLeads(r.data || []))
    .catch((e) => toast.error(apiErr(e, "Could not load leads"))).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const byStage = useMemo(() => {
    const m = Object.fromEntries(STAGES.map((s) => [s.k, []]));
    leads.forEach((l) => { (m[l.stage] || m.new).push(l); });
    return m;
  }, [leads]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (l) => {
    setEditing(l.lead_id);
    setForm({ ...EMPTY, ...l, estimated_value: l.estimated_value || "" });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Contact name is required"); return; }
    setSaving(true);
    try {
      const payload = { ...form, estimated_value: parseFloat(form.estimated_value) || 0 };
      if (editing) { await api.patch(`/leads/${editing}`, payload); toast.success("Lead updated"); }
      else { await api.post("/leads", payload); toast.success("Lead added"); }
      setOpen(false); load();
    } catch (e) { toast.error(apiErr(e, "Failed to save lead")); }
    finally { setSaving(false); }
  };

  const moveStage = async (l, stage) => {
    setLeads((p) => p.map((x) => (x.lead_id === l.lead_id ? { ...x, stage } : x)));
    try { await api.patch(`/leads/${l.lead_id}`, { stage }); }
    catch (e) { toast.error(apiErr(e, "Failed to move lead")); load(); }
  };

  const remove = async (l) => {
    try { await api.delete(`/leads/${l.lead_id}`); toast.success("Lead deleted"); load(); }
    catch (e) { toast.error(apiErr(e, "Failed to delete")); }
  };

  const convert = async (l) => {
    try {
      const r = await api.post(`/leads/${l.lead_id}/convert`);
      toast.success("Converted to project");
      navigate(`/project/${r.data.project_id}`);
    } catch (e) { toast.error(apiErr(e, "Failed to convert")); }
  };

  const totalOpen = leads.filter((l) => !["won", "lost"].includes(l.stage)).reduce((s, l) => s + (l.estimated_value || 0), 0);
  const now = new Date();
  const wonThisMonth = leads.filter((l) => {
    if (l.stage !== "won" || !l.updated_at) return false;
    const d = new Date(l.updated_at);
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  }).length;
  const wonTotal = leads.filter((l) => l.stage === "won").length;
  const decided = leads.filter((l) => l.stage === "won" || l.stage === "lost").length;
  const conversionRate = decided ? Math.round((wonTotal / decided) * 100) : 0;

  const stats = [
    { label: "Won this month", value: wonThisMonth, testid: "crm-stat-won-month" },
    { label: "Conversion rate", value: `${conversionRate}%`, sub: `${wonTotal} won · ${decided} closed`, testid: "crm-stat-conversion" },
    { label: "Open pipeline", value: money(totalOpen) || "$0", testid: "crm-stat-pipeline" },
  ];

  return (
    <div className="p-6 lg:p-8" data-testid="crm-page">
      <header className="mb-4 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-4xl font-black tracking-tighter">Leads</h1>
          <p className="text-zinc-500 mt-2 text-sm">{leads.length} leads in the pipeline</p>
        </div>
        <Button onClick={openNew} className="bg-zinc-900 hover:bg-zinc-800 rounded-sm gap-2" data-testid="add-lead-button">
          <Plus className="h-4 w-4" /> Add Lead
        </Button>
      </header>

      <div className="grid grid-cols-3 gap-3 mb-6 max-w-xl" data-testid="crm-stats">
        {stats.map((s) => (
          <div key={s.label} className="bg-white border border-zinc-200 rounded-sm px-4 py-3" data-testid={s.testid}>
            <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500">{s.label}</p>
            <p className="font-heading text-2xl font-black mt-1 text-zinc-900">{s.value}</p>
            {s.sub && <p className="text-[11px] text-zinc-400 mt-0.5">{s.sub}</p>}
          </div>
        ))}
      </div>

      {loading ? (
        <div className="h-8 w-8 border-2 border-zinc-900 border-t-transparent rounded-full animate-spin" />
      ) : (
        <div className="flex gap-3 overflow-x-auto pb-4">
          {STAGES.map((s) => {
            const items = byStage[s.k] || [];
            const val = items.reduce((a, l) => a + (l.estimated_value || 0), 0);
            return (
              <div key={s.k} className="w-72 shrink-0" data-testid={`lead-col-${s.k}`}>
                <div className="flex items-center justify-between px-1 mb-2">
                  <span className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-zinc-600">
                    <span className="h-2.5 w-2.5 rounded-full" style={{ background: s.dot }} />{s.label} · {items.length}
                  </span>
                  {val > 0 && <span className="text-[11px] text-zinc-400">{money(val)}</span>}
                </div>
                <div className="bg-zinc-100/70 rounded-sm p-2 space-y-2 min-h-[120px]">
                  {items.length === 0 && <p className="text-xs text-zinc-400 text-center py-6">No leads</p>}
                  {items.map((l) => {
                    const isCompany = !!(l.company || "").trim();
                    return (
                      <div key={l.lead_id} className="bg-white border border-zinc-200 rounded-sm p-3" data-testid={`lead-card-${l.lead_id}`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="text-sm font-semibold truncate flex items-center gap-1.5">
                              {isCompany ? <Building2 className="h-3.5 w-3.5 text-zinc-400 shrink-0" /> : <Contact2 className="h-3.5 w-3.5 text-zinc-400 shrink-0" />}
                              {l.company || l.name}
                            </p>
                            {l.company && <p className="text-xs text-zinc-500 truncate">{l.name}</p>}
                          </div>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button className="text-zinc-400 hover:text-zinc-900 shrink-0" data-testid={`lead-menu-${l.lead_id}`}><MoreVertical className="h-4 w-4" /></button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="rounded-sm">
                              <DropdownMenuItem onClick={() => openEdit(l)} data-testid={`lead-edit-${l.lead_id}`}><Pencil className="h-3.5 w-3.5 mr-2" /> Edit</DropdownMenuItem>
                              {l.converted_project_id ? (
                                <DropdownMenuItem onClick={() => navigate(`/project/${l.converted_project_id}`)}><ArrowRightCircle className="h-3.5 w-3.5 mr-2" /> Open project</DropdownMenuItem>
                              ) : (
                                <DropdownMenuItem onClick={() => convert(l)} data-testid={`lead-convert-${l.lead_id}`}><ArrowRightCircle className="h-3.5 w-3.5 mr-2" /> Convert to project</DropdownMenuItem>
                              )}
                              <DropdownMenuItem onClick={() => remove(l)} className="text-red-600" data-testid={`lead-delete-${l.lead_id}`}><Trash2 className="h-3.5 w-3.5 mr-2" /> Delete</DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                        {l.estimated_value > 0 && <p className="text-sm font-heading font-black mt-1.5">{money(l.estimated_value)}</p>}
                        <div className="mt-1.5 space-y-0.5 text-xs text-zinc-500">
                          {l.email && <p className="flex items-center gap-1.5 truncate"><Mail className="h-3 w-3 shrink-0" />{l.email}</p>}
                          {l.phone && <p className="flex items-center gap-1.5 truncate"><Phone className="h-3 w-3 shrink-0" />{l.phone}</p>}
                          {l.next_follow_up && <p className="flex items-center gap-1.5"><CalendarClock className="h-3 w-3 shrink-0" />{l.next_follow_up}</p>}
                        </div>
                        <div className="mt-2 flex items-center gap-1.5">
                          <span className="text-[10px] uppercase tracking-wider text-zinc-400 border border-zinc-200 rounded-sm px-1.5 py-0.5">{l.project_type}</span>
                          {l.converted_project_id && <span className="text-[10px] uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-sm px-1.5 py-0.5">Converted</span>}
                        </div>
                        <Select value={l.stage} onValueChange={(v) => moveStage(l, v)}>
                          <SelectTrigger className="rounded-sm h-7 mt-2 text-xs" data-testid={`lead-stage-${l.lead_id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{STAGES.map((st) => <SelectItem key={st.k} value={st.k}>{st.label}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Edit lead" : "Add lead"}</DialogTitle>
            <DialogDescription>Track a potential client through your pipeline. Only the contact name is required.</DialogDescription>
          </DialogHeader>
          <LeadForm value={form} onChange={setForm} />
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-zinc-900 hover:bg-zinc-800 rounded-sm" data-testid="save-lead-button">
              {saving ? "Saving…" : editing ? "Save changes" : "Add lead"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
